import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const MaintenancePage = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 2,
    minutes: 30,
    seconds: 0
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime.hours === 0 && prevTime.minutes === 0 && prevTime.seconds === 0) {
          return prevTime;
        }

        let newSeconds = prevTime.seconds - 1;
        let newMinutes = prevTime.minutes;
        let newHours = prevTime.hours;

        if (newSeconds < 0) {
          newSeconds = 59;
          newMinutes -= 1;
        }

        if (newMinutes < 0) {
          newMinutes = 59;
          newHours -= 1;
        }

        if (newHours < 0) {
          return { hours: 0, minutes: 0, seconds: 0 };
        }

        return { hours: newHours, minutes: newMinutes, seconds: newSeconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (value) => {
    return value.toString().padStart(2, '0');
  };

  return (
    <>
      <Helmet>
        <title>Scheduled Maintenance | Abacies Logiciels</title>
        <meta name="description" content="We're currently performing scheduled maintenance to improve our services. We'll be back soon!" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-alabaster to-pearl-white flex items-center justify-center px-4">
        <div className="max-w-3xl w-full text-center">
          {/* Maintenance Icon */}
          <div className="mb-8 animate-fade-in">
            <div className="relative inline-block">
              <div className="w-32 h-32 md:w-48 md:h-48 bg-blush-light/30 rounded-full flex items-center justify-center">
                <svg className="w-20 h-20 md:w-32 md:h-32 text-primary-red animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="absolute -bottom-2 -right-2 bg-primary-red text-white px-3 py-1 rounded-full text-sm font-semibold">
                In Progress
              </div>
            </div>
          </div>

          {/* Main Message */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-charcoal mb-4 animate-slide-up">
            We'll Be Right Back!
          </h1>
          <p className="text-xl text-titanium mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
            We're currently performing scheduled maintenance to improve our services and bring you new features.
          </p>

          {/* Countdown Timer */}
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <p className="text-titanium mb-4">Estimated time remaining:</p>
            <div className="flex justify-center items-center space-x-4">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary-red">
                  {formatTime(timeLeft.hours)}
                </div>
                <p className="text-sm text-titanium mt-1">Hours</p>
              </div>
              <div className="text-3xl md:text-4xl font-bold text-charcoal">:</div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary-red">
                  {formatTime(timeLeft.minutes)}
                </div>
                <p className="text-sm text-titanium mt-1">Minutes</p>
              </div>
              <div className="text-3xl md:text-4xl font-bold text-charcoal">:</div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary-red">
                  {formatTime(timeLeft.seconds)}
                </div>
                <p className="text-sm text-titanium mt-1">Seconds</p>
              </div>
            </div>
          </div>

          {/* What We're Doing */}
          <div className="bg-white rounded-lg p-6 mb-8 animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <h2 className="text-lg font-semibold text-charcoal mb-4">What We're Working On:</h2>
            <div className="grid md:grid-cols-3 gap-4 text-sm text-titanium">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-medium text-charcoal">System Updates</p>
                  <p>Installing latest security patches</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-5 h-5 mr-2 mt-0.5">
                  <div className="w-5 h-5 border-2 border-primary-red rounded-full animate-spin"></div>
                </div>
                <div>
                  <p className="font-medium text-charcoal">Performance</p>
                  <p>Optimizing server response times</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-5 h-5 mr-2 mt-0.5">
                  <div className="w-5 h-5 border-2 border-cool-grey rounded-full"></div>
                </div>
                <div>
                  <p className="font-medium text-charcoal">New Features</p>
                  <p>Deploying exciting updates</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stay Connected */}
          <div className="bg-blush-light/20 rounded-lg p-6 mb-8 animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <h3 className="text-lg font-semibold text-charcoal mb-3">Stay Connected</h3>
            <p className="text-sm text-titanium mb-4">
              Follow us for real-time updates on the maintenance progress:
            </p>
            <div className="flex justify-center space-x-4">
              <a href="#" className="w-10 h-10 bg-white rounded-lg flex items-center justify-center hover:bg-primary-red hover:text-white transition-all duration-300">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
                </svg>
              </a>
              <a href="#" className="w-10 h-10 bg-white rounded-lg flex items-center justify-center hover:bg-primary-red hover:text-white transition-all duration-300">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Contact Information */}
          <div className="text-sm text-titanium animate-fade-in" style={{ animationDelay: '0.5s' }}>
            <p>
              Need immediate assistance? Our support team is available at{' '}
              <a href="mailto:support@abacies.com" className="text-primary-red hover:underline">
                support@abacies.com
              </a>
            </p>
            <p className="mt-2">
              Or call us at <span className="font-semibold">+1 (555) 123-4567</span>
            </p>
          </div>

          {/* Auto-refresh Notice */}
          <div className="mt-8 text-xs text-cool-grey animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <p>This page will automatically refresh when maintenance is complete.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default MaintenancePage;
